import { useQueryClient } from "@tanstack/react-query";
import { Paperclip, Send, X } from "lucide-react";
import {
  useDataProvider,
  useGetList,
  useNotify,
  useTranslate,
  type Identifier,
} from "ra-core";
import { useId, useRef, useState, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

import type { CrmDataProvider } from "../providers/types";
import type { MailCard, UploadedMailFile } from "../providers/vv/dataProvider";
import type { MailTemplate } from "../types";
import { htmlToText, textToHtml } from "./mailHtml";

const MAX_TOTAL = 20 * 1024 * 1024;
const NO_TEMPLATE = "none";

const errorMessage = (error: unknown) =>
  (error as { body?: { message?: string } })?.body?.message ??
  (error as Error)?.message ??
  String(error);

const splitAddresses = (s: string) =>
  s
    .split(/[,;\s]+/)
    .map((a) => a.trim())
    .filter(Boolean);

/**
 * «Написать письмо»: To (filled from the card), Cc, a template (filled by
 * the server for this card, then editable), the subject, the text and files.
 * The letter goes out through crm-api; a refusal shows the server's reason
 * and the window stays open (the card's feed shows it as «не отправлено»).
 */
export const MailComposer = ({
  open,
  onOpenChange,
  card,
  defaultTo,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  card: MailCard;
  defaultTo?: string | null;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const queryClient = useQueryClient();
  const { data: templates } = useGetList<MailTemplate>(
    "mail_templates",
    {
      sort: { field: "name", order: "ASC" },
      pagination: { page: 1, perPage: 100 },
    },
    { enabled: open },
  );
  const [to, setTo] = useState(defaultTo ?? "");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState("");
  const [text, setText] = useState("");
  const [template, setTemplate] = useState<string>(NO_TEMPLATE);
  const [files, setFiles] = useState<UploadedMailFile[]>([]);
  const [busy, setBusy] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const ids = { to: useId(), cc: useId(), subject: useId(), body: useId() };

  const reset = () => {
    setTo(defaultTo ?? "");
    setCc("");
    setSubject("");
    setText("");
    setTemplate(NO_TEMPLATE);
    setFiles([]);
  };

  const fail = (error: unknown) =>
    notify(errorMessage(error), {
      type: "error",
      messageArgs: { _: errorMessage(error) },
    });

  const pickTemplate = async (value: string | null) => {
    const id = value ?? NO_TEMPLATE;
    setTemplate(id);
    if (id === NO_TEMPLATE) return;
    try {
      const r = await dataProvider.renderMailTemplate(id as Identifier, card);
      setSubject(r.subject);
      setText(htmlToText(r.html));
    } catch (error) {
      fail(error);
    }
  };

  const attach = async (list: FileList | null) => {
    if (!list?.length) return;
    setBusy(true);
    try {
      const added: UploadedMailFile[] = [];
      for (const f of Array.from(list)) {
        added.push(await dataProvider.uploadMailFile(f));
      }
      setFiles((prev) => [...prev, ...added]);
    } catch (error) {
      notify("crm.mail.upload_error", { type: "error" });
      console.warn(error);
    } finally {
      setBusy(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const total = files.reduce((s, f) => s + f.size, 0);
  const canSend =
    !busy &&
    splitAddresses(to).length > 0 &&
    subject.trim() !== "" &&
    text.trim() !== "" &&
    total <= MAX_TOTAL;

  const send = async () => {
    setBusy(true);
    try {
      const r = await dataProvider.sendMail({
        ...card,
        to: splitAddresses(to),
        cc: splitAddresses(cc),
        subject: subject.trim(),
        html: textToHtml(text),
        attachments: files.map((f) => f.path),
      });
      if (r.refused?.length) {
        notify("crm.mail.sent_except", {
          type: "warning",
          messageArgs: { list: r.refused.join(", ") },
        });
      } else {
        notify("crm.mail.sent", { type: "success" });
      }
      reset();
      onOpenChange(false);
    } catch (error) {
      const text = `${translate("crm.mail.send_error")}. ${errorMessage(error).replace(/^Письмо не отправлено: /, "")}`;
      notify(text, { type: "error", messageArgs: { _: text } });
    } finally {
      setBusy(false);
      // a refused letter is in the feed as «не отправлено»; a sent one adds a note
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["mail_messages"] }),
        queryClient.invalidateQueries({ queryKey: ["deal_notes"] }),
        queryClient.invalidateQueries({ queryKey: ["contact_notes"] }),
      ]);
    }
  };

  const templateItems = [
    { value: NO_TEMPLATE, label: translate("crm.mail.no_template") },
    ...(templates ?? []).map((t) => ({ value: String(t.id), label: t.name })),
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{translate("crm.mail.write")}</DialogTitle>
        </DialogHeader>
        <form
          className="flex flex-col gap-3 min-w-0"
          onSubmit={(event) => {
            event.preventDefault();
            if (canSend) void send();
          }}
        >
          <Field id={ids.to} label={translate("crm.mail.to")}>
            <Input
              id={ids.to}
              type="text"
              inputMode="email"
              autoComplete="off"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="client@example.com"
            />
          </Field>
          <Field
            id={ids.cc}
            label={translate("crm.mail.cc")}
            hint={translate("crm.mail.to_hint")}
          >
            <Input
              id={ids.cc}
              type="text"
              inputMode="email"
              autoComplete="off"
              value={cc}
              onChange={(e) => setCc(e.target.value)}
            />
          </Field>
          {templates?.length ? (
            <div className="flex flex-col gap-1.5 min-w-0">
              <span className="text-sm">{translate("crm.mail.template")}</span>
              <Select
                value={template}
                onValueChange={(v) => void pickTemplate(v as string | null)}
                items={templateItems}
              >
                <SelectTrigger
                  className="w-full"
                  aria-label={translate("crm.mail.template")}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {templateItems.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}
          <Field id={ids.subject} label={translate("crm.mail.subject")}>
            <Input
              id={ids.subject}
              value={subject}
              maxLength={300}
              onChange={(e) => setSubject(e.target.value)}
            />
          </Field>
          <Field id={ids.body} label={translate("crm.mail.body")}>
            <Textarea
              id={ids.body}
              value={text}
              rows={8}
              onChange={(e) => setText(e.target.value)}
              className="min-h-40"
            />
          </Field>
          <div className="flex flex-col gap-1.5 min-w-0">
            {files.length ? (
              <ul className="flex flex-wrap gap-2">
                {files.map((f) => (
                  <li
                    key={f.path}
                    className="flex items-center gap-1 rounded-md border px-2 py-1 text-xs max-w-full min-w-0"
                  >
                    <span className="truncate">{f.name}</span>
                    <button
                      type="button"
                      className="shrink-0 text-muted-foreground hover:text-foreground"
                      aria-label={`${translate("crm.mail.remove_file")}: ${f.name}`}
                      onClick={() =>
                        setFiles((prev) =>
                          prev.filter((x) => x.path !== f.path),
                        )
                      }
                    >
                      <X className="size-3" />
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={busy}
                onClick={() => fileInput.current?.click()}
              >
                <Paperclip className="size-4" />
                {translate("crm.mail.attach")}
              </Button>
              <span
                className={
                  total > MAX_TOTAL
                    ? "text-xs text-destructive"
                    : "text-xs text-muted-foreground"
                }
              >
                {translate("crm.mail.attach_hint")}
              </span>
              <input
                ref={fileInput}
                type="file"
                multiple
                hidden
                data-testid="mail-file"
                onChange={(e) => void attach(e.target.files)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={!canSend}>
              <Send className="size-4" />
              {translate("crm.mail.send")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({
  id,
  label,
  hint,
  children,
}: {
  id: string;
  label: string;
  hint?: string;
  children: ReactNode;
}) => (
  <div className="flex flex-col gap-1.5 min-w-0">
    <label htmlFor={id} className="text-sm">
      {label}
    </label>
    {children}
    {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
  </div>
);
